/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Exam, Question, QuestionType, Answer, ExamCategory } from '../types';
import { questionApi, categoryApi, imageApi } from '../services/api';
import QuestionEditor from '../components/QuestionEditor';
import { SHOW_GEOMETRIC_SHAPES } from '../config/uiFlags';

// A very small helper to extract tags list from loaded questions
const collectAllTags = (questions: Question[]): string[] => {
    const set = new Set<string>();
    questions.forEach(q => (q.tags || []).forEach(t => set.add(t)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
};

const emptyQuestion = (type: QuestionType): Question => ({
    questionText: '',
    questionRichText: '',
    shapesJson: '[]',
    imagePath: '',
    imageAsPrimary: false,
    subject: '',
    classLevel: '',
    complexity: undefined,
    tags: [],
    questionType: type,
    marks: 1,
    answers: type === 'MCQ' ? [
        { answerText: '', isCorrect: false },
        { answerText: '', isCorrect: false },
        { answerText: '', isCorrect: false },
        { answerText: '', isCorrect: true },
    ] : [],
});

const Questions: React.FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Subject categories and classes for dropdowns
    const [categories, setCategories] = useState<ExamCategory[]>([]);
    const defaultClasses = ['Open','1st Class','2nd Class','3rd Class','4th Class','5th Class','6th Class','7th Class','8th Class','9th Class','10th Class','11th Class','12th Class'];
    const [classes] = useState<string[]>(defaultClasses);

    // Standalone questions loaded from server-managed bank
    const [questions, setQuestions] = useState<Question[]>([]);

    // UI State
    const [search, setSearch] = useState('');
    const [filterSubject, setFilterSubject] = useState('');
    const [filterClass, setFilterClass] = useState('');

    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editing, setEditing] = useState<Question | null>(null);

    const allTags = useMemo(() => collectAllTags(questions), [questions]);
    const [tagInput, setTagInput] = useState('');

    // Pagination state (server-side)
    const [page, setPage] = useState(0); // zero-based
    const [size] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // Refs for filter inputs to manage datalist behavior and reset focus
    const searchRef = useRef<HTMLInputElement | null>(null);
    const subjectRef = useRef<HTMLInputElement | null>(null);
    const classRef = useRef<HTMLInputElement | null>(null);

    // Close datalist suggestions after user picks an option
    const maybeCloseDatalist = (e: React.FormEvent<HTMLInputElement>, ref: React.RefObject<HTMLInputElement>) => {
        const ne: any = (e as any).nativeEvent;
        const inputType = ne && ne.inputType;
        // When a datalist option is chosen, many browsers emit inputType = 'insertReplacementText'
        if (inputType === 'insertReplacementText') {
            // Briefly blur and refocus to close the datalist popup
            const el = ref.current;
            if (el) {
                el.blur();
                // queue refocus to maintain good UX
                setTimeout(() => el.focus(), 0);
            }
        }
    };

    const resetFilters = () => {
        setSearch('');
        setFilterSubject('');
        setFilterClass('');
        setPage(0);
        // Ensure all inputs lose any open datalist then focus the first
        if (subjectRef.current) subjectRef.current.blur();
        if (classRef.current) classRef.current.blur();
        if (searchRef.current) {
            searchRef.current.blur();
            setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
        }
    };

    // Rich-text editor helpers to mirror Create Exam look & feel
    // Using shared QuestionEditor for rich text and image; local helpers removed

    // Load categories once
    useEffect(() => {
        const loadCats = async () => {
            try {
                const cats = await categoryApi.list();
                setCategories(cats);
            } catch (e) {
                console.error('Failed to load categories', e);
            }
        };
        loadCats();
    }, []);

    const fetchPage = async (targetPage: number = page) => {
        if (!user?.id) return;
        try {
            setLoading(true);
            setError(null);
            const params = {
                page: targetPage,
                size,
                search: search || undefined,
                subject: filterSubject || undefined,
                classLevel: filterClass || undefined,
            } as any;
            if (user.role === 'TEACHER') {
                const res = await questionApi.listByTeacherPaged(user.id, params);
                setQuestions(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
                setPage(res.page ?? targetPage);
            } else if (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN') {
                const selInstRaw = localStorage.getItem('admin_selected_institution_id');
                const selectedInstitutionId = selInstRaw ? Number(selInstRaw) : 0;
                const res = await questionApi.listForAdminPaged(user.id, {
                    ...params,
                    institutionId: user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined,
                });
                setQuestions(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
                setPage(res.page ?? targetPage);
            } else {
                const res = await questionApi.listByTeacherPaged(user.id, params);
                setQuestions(res.content || []);
                setTotalPages(res.totalPages || 0);
                setTotalElements(res.totalElements || 0);
                setPage(res.page ?? targetPage);
            }
        } catch (e) {
            console.error('Failed to load questions', e);
            setError('Failed to load questions');
        } finally {
            setLoading(false);
        }
    };

    // Fetch whenever user, page, or filters change
    useEffect(() => {
        if (!user?.id) return;
        fetchPage(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.role, page, search, filterSubject, filterClass]);


    // Server already applies filters and pagination; just use the current page content
    const filtered = useMemo(() => questions, [questions]);

    const startAdd = (type: QuestionType) => {
        setEditingIndex(null);
        setEditing(emptyQuestion(type));
        setTagInput('');
    };

    const startEdit = (idx: number) => {
        setEditingIndex(idx);
        setEditing({ ...(filtered[idx]) });
        setTagInput('');
    };

    const cancelEdit = () => {
        setEditing(null);
        setEditingIndex(null);
        setTagInput('');
    };

    const updateEditing = (patch: Partial<Question>) => {
        setEditing(prev => prev ? { ...prev, ...patch } : prev);
    };

    const toggleCorrect = (ansIndex: number) => {
        if (!editing || editing.questionType !== 'MCQ') return;
        const updated = (editing.answers || []).map((a, i) => ({ ...a, isCorrect: i === ansIndex }));
        updateEditing({ answers: updated });
    };

    const addAnswer = () => {
        if (!editing || editing.questionType !== 'MCQ') return;
        const updated = [ ...(editing.answers || []), { answerText: '', isCorrect: false } as Answer ];
        updateEditing({ answers: updated });
    };

    const removeAnswer = (i: number) => {
        if (!editing || editing.questionType !== 'MCQ') return;
        const updated = (editing.answers || []).filter((_, idx) => idx !== i);
        updateEditing({ answers: updated });
    };

    const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            const value = tagInput.trim();
            if (!value) return;
            const current = new Set(editing?.tags || []);
            current.add(value);
            updateEditing({ tags: Array.from(current) });
            setTagInput('');
        }
        if (e.key === 'Backspace' && !tagInput && (editing?.tags || []).length > 0) {
            const copy = [ ...(editing?.tags || []) ];
            copy.pop();
            updateEditing({ tags: copy });
        }
    };

    const removeTag = (tag: string) => {
        const remaining = (editing?.tags || []).filter(t => t !== tag);
        updateEditing({ tags: remaining });
    };

    const pickSuggestion = (tag: string) => {
        const current = new Set(editing?.tags || []);
        current.add(tag);
        updateEditing({ tags: Array.from(current) });
        setTagInput('');
    };

    const uploadImage = async (file: File) => {
        try {
            const ALLOWED_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']);
            const ALLOWED_LABEL = 'JPG, JPEG, PNG, WEBP, GIF';
            if (!file.type || !ALLOWED_MIME.has(file.type.toLowerCase())) {
                alert(`Only ${ALLOWED_LABEL} images are allowed`);
                return;
            }
            const MAX_UPLOAD_BUTTON = 5 * 1024 * 1024; // 5MB
            if (file.size > MAX_UPLOAD_BUTTON) {
                alert('Uploaded image must be under 5MB');
                return;
            }
            const { url } = await imageApi.upload(file, 'button');
            updateEditing({ imagePath: url });
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Failed to upload image';
            alert(msg);
        }
    };

    const validate = (q: Question | null): string | null => {
        if (!q) return 'No data';
        if (!q.subject || !q.subject.trim()) return 'Subject is required';
        if (!q.classLevel || !q.classLevel.trim()) return 'Class is required';
        if (!q.marks || q.marks <= 0) return 'Marks should be positive';
        if (q.questionType === 'MCQ') {
            const answers = q.answers || [];
            if (answers.length < 4) return 'MCQ must have at least 4 options';
            const correct = answers.filter(a => a.isCorrect).length;
            if (correct !== 1) return 'Exactly one correct answer is required';
        }
        if (!q.imageAsPrimary) {
            const text = (q.questionText || '').trim() || (q.questionRichText || '').replace(/<[^>]+>/g, ' ').trim();
            if (!text) return 'Question text or primary image is required';
        } else if (!q.imagePath || !q.imagePath.trim()) {
            return 'Primary image is selected but no image uploaded';
        }
        return null;
    };

    const reloadQuestions = async () => {
        await fetchPage(page);
    };

    const save = async () => {
        if (!user?.id || !editing) return;
        const err = validate(editing);
        if (err) {
            alert(err);
            return;
        }
        try {
            if (editingIndex == null) {
                // Create
                await questionApi.create(editing, user.id);
            } else {
                const q = filtered[editingIndex];
                await questionApi.update(q.id!, editing);
            }
            await reloadQuestions();
            cancelEdit();
        } catch (e) {
            console.error('Failed to save question', e);
            alert('Failed to save question');
        }
    };

    const deleteQuestion = async (qid: number) => {
        if (!window.confirm('Delete this question?')) return;
        try {
            await questionApi.delete(qid);
            await reloadQuestions();
        } catch (e) {
            console.error('Failed to delete question', e);
            alert('Failed to delete question');
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                <h1 style={{ margin: 0 }}>📝 Questions</h1>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-primary" onClick={() => startAdd('MCQ')}>➕ Add MCQ</button>
                    <button className="btn btn-outline" onClick={() => startAdd('SUBJECTIVE')}>➕ Add Subjective</button>
                    <Link to="/create-exam" className="btn btn-secondary">Create Exam</Link>
                </div>
            </div>

            {/* Filters */}
            <div className="card" style={{ marginTop: '1rem' }}>
                {/* Keep all three inputs in a single line on typical desktop widths */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px 200px auto', gap: '0.75rem', alignItems: 'start' }}>
                    {/* Search/Text or Tag with suggestions for Tags */}
                    <div style={{ width: '100%' }}>
                        <input
                            ref={searchRef}
                            type="text"
                            className="form-input"
                            style={{ width: '100%' }}
                            placeholder="Search by text or tag..."
                            value={search}
                            list="tags-suggestions"
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            onInput={(e) => maybeCloseDatalist(e, searchRef)}
                        />
                        <datalist id="tags-suggestions">
                            {allTags
                                .filter(t => !search ? true : t.toLowerCase().includes(search.toLowerCase()))
                                .slice(0, 20)
                                .map((t, i) => (
                                    <option key={i} value={t}>{t}</option>
                                ))}
                        </datalist>
                    </div>

                    {/* Subject with suggestions from Categories */}
                    <div style={{ width: '100%' }}>
                        <input
                            ref={subjectRef}
                            type="text"
                            className="form-input"
                            style={{ width: '100%' }}
                            placeholder="Filter by Subject"
                            value={filterSubject}
                            list="subject-suggestions"
                            onChange={(e) => { setFilterSubject(e.target.value); setPage(0); }}
                            onInput={(e) => maybeCloseDatalist(e, subjectRef)}
                        />
                        <datalist id="subject-suggestions">
                            {categories
                                .map(c => c.name)
                                .filter((v, i, arr) => v && arr.indexOf(v) === i)
                                .filter(s => !filterSubject ? true : (s || '').toLowerCase().includes(filterSubject.toLowerCase()))
                                .slice(0, 20)
                                .map((s, i) => (
                                    <option key={i} value={s}>{s}</option>
                                ))}
                        </datalist>
                    </div>

                    {/* Class with suggestions from known classes */}
                    <div style={{ width: '100%' }}>
                        <input
                            ref={classRef}
                            type="text"
                            className="form-input"
                            style={{ width: '100%' }}
                            placeholder="Filter by Class"
                            value={filterClass}
                            list="class-suggestions"
                            onChange={(e) => setFilterClass(e.target.value)}
                            onInput={(e) => maybeCloseDatalist(e, classRef)}
                        />
                        <datalist id="class-suggestions">
                            {classes
                                .filter(c => !filterClass ? true : c.toLowerCase().includes(filterClass.toLowerCase()))
                                .slice(0, 20)
                                .map((c, i) => (
                                    <option key={i} value={c}>{c}</option>
                                ))}
                        </datalist>
                    </div>

                    {/* Reset button */}
                    <div style={{ display: 'flex' }}>
                        <button type="button" className="btn btn-outline" onClick={resetFilters} title="Clear all filters">Reset</button>
                    </div>
                </div>
            </div>

            {/* List */}
            {filtered.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-emoji">🔎</div>
                    <h3 className="empty-state-title">No Questions Found</h3>
                    <p>Use the Add buttons to create questions.</p>
                </div>
            ) : (
                <div className="table-container" style={{ marginTop: '1rem' }}>
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Subject</th>
                                <th>Class</th>
                                <th>Type</th>
                                <th>Marks</th>
                                <th>Tags</th>
                                <th>Preview</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((q, idx) => (
                                <tr key={q.id || idx}>
                                    <td>{q.subject || '-'}</td>
                                    <td>{q.classLevel || '-'}</td>
                                    <td>{q.questionType}</td>
                                    <td>{q.marks}</td>
                                    <td>
                                        {(q.tags || []).slice(0, 3).map((t, i) => (
                                            <span key={i} className="badge badge-info" style={{ marginRight: 4 }}>{t}</span>
                                        ))}
                                        {(q.tags || []).length > 3 && <span>+{(q.tags || []).length - 3}</span>}
                                    </td>
                                    <td style={{ maxWidth: 320 }}>
                                        {q.imageAsPrimary && q.imagePath ? (
                                            <img src={q.imagePath} alt="q" style={{ maxHeight: 60 }} />
                                        ) : (
                                            <span title={(q.questionText || '').substring(0, 200)}>
                                                {(q.questionText || '').substring(0, 60)}
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            <button className="btn btn-outline btn-sm" onClick={() => startEdit(idx)}>✏️ Edit</button>
                                            {q.id && <button className="btn btn-danger btn-sm" onClick={() => deleteQuestion(q.id!)}>🗑 Delete</button>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Pagination Controls */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <div>
                            Showing {filtered.length} of {totalElements} results
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                            <button className="btn btn-outline btn-sm" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</button>
                            {Array.from({ length: totalPages }).slice(Math.max(0, page - 2), Math.min(totalPages, page + 3)).map((_, i) => {
                                const pageNum = Math.max(0, Math.min(totalPages - 1, (page - 2) + i));
                                // If totalPages small, recalc to actual index
                                const idx = totalPages <= 5 ? i : pageNum;
                                const label = (idx + 1);
                                const active = idx === page;
                                return (
                                    <button key={idx} className={"btn btn-sm " + (active ? 'btn-primary' : 'btn-outline')} onClick={() => setPage(idx)} disabled={active}>{label}</button>
                                );
                            })}
                            <button className="btn btn-outline btn-sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Next</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Editor */}
            {editing && (
                <div className="modal" style={{ display: 'block', width: 'min(95vw, 1200px)', maxWidth: '100%', maxHeight: 'none', overflowY: 'visible' }}>
                    <div className="modal-content" style={{ width: 'min(95vw, 1200px)', maxWidth: '100%' }}>
                        <div className="modal-header">
                            <h3>{editingIndex == null ? 'Add Question' : 'Edit Question'}</h3>
                            <button className="close" onClick={cancelEdit}>×</button>
                        </div>
                        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 320px) 1fr', gap: '1rem', alignItems: 'start' }}>
                            {/* Left: metadata (Complexity and Tags). Subject/Class are now in the shared editor on the right. */}
                            <div>
                                <label className="form-label" style={{ marginTop: 8 }}>Complexity</label>
                                <select className="form-input" value={editing.complexity || ''} onChange={e => updateEditing({ complexity: (e.target.value || undefined) as any })}>
                                    <option value="">Select complexity</option>
                                    <option value="easy">easy</option>
                                    <option value="medium">medium</option>
                                    <option value="hard">hard</option>
                                    <option value="very hard">very hard</option>
                                </select>

                                <label className="form-label" style={{ marginTop: 8 }}>Tags</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, border: '1px solid #e2e8f0', borderRadius: 6, padding: 6 }}>
                                    {(editing.tags || []).map((t, i) => (
                                        <span key={i} className="badge badge-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                            {t}
                                            <button type="button" className="btn btn-xs" onClick={() => removeTag(t)}>×</button>
                                        </span>
                                    ))}
                                    <input
                                        className="form-input"
                                        style={{ border: 'none', outline: 'none', flex: '1 1 120px', minWidth: 120 }}
                                        placeholder="Type and press Enter to add tag"
                                        value={tagInput}
                                        onChange={e => setTagInput(e.target.value)}
                                        onKeyDown={handleTagKeyDown}
                                    />
                                </div>
                                {/* Suggestions */}
                                {tagInput && (
                                    <div className="card" style={{ marginTop: 6 }}>
                                        {(allTags.filter(t => t.toLowerCase().includes(tagInput.toLowerCase()))).slice(0, 8).map((t, i) => (
                                            <div key={i} className="row" style={{ cursor: 'pointer' }} onClick={() => pickSuggestion(t)}>{t}</div>
                                        ))}
                                        {allTags.length === 0 && <div className="row">No existing tags</div>}
                                    </div>
                                )}
                            </div>

                            {/* Right: shared Question Editor with Subject/Class dropdowns */}
                            <div style={{ minWidth: 0 }}>
                                <QuestionEditor
                                    question={editing}
                                    onChange={(patch) => updateEditing(patch)}
                                    showSubjectClass={true}
                                    showShapesEditor={SHOW_GEOMETRIC_SHAPES}
                                    categories={categories}
                                    classes={classes}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn" onClick={cancelEdit}>Cancel</button>
                            <button className="btn btn-primary" onClick={save}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Questions;
